 
import {
  AlignmentMetric,
  CaseReport,
  MetricComparison,
  MetricSession,
  MetricSessionTypes
} from './CaseTypes';

const SEGMENTAL_MEASUREMENT_LABELS = ['Sagittal Angle', 'Coronal Angle'];
const SACRUM_LABEL = 'Sacrum';

export default class AchievedCorrectionViewModel {
  caseReport: CaseReport;

  constructor(caseReport: CaseReport) {
    this.caseReport = caseReport;
  }

  calculateDifference = (preValue: number|null|undefined, postValue: number|null|undefined): number => {
    if (preValue === undefined || preValue === null ||
        postValue === undefined || postValue === null) {
      return 0;
    }

    return postValue - preValue;
  };

  formatDifference = (diff: number|null, unit: string): { text: string } | null => {
    if (diff === null || diff === undefined) return null;

    const prefix = diff > 0 ? '+' : '';
    return {
      text: `(${prefix}${diff.toFixed(0)}${unit || ''})`
    };
  };

  formatValue = (value: string|number, unit: string): string => {
    if (value === undefined || value === null) return 'N/A';
    if (typeof value !== 'string') {
      value = value.toFixed(0);
    }
    return `${value}${unit || ''}`;
  };

  getMetricSessions(): MetricSession[] {
    return this.caseReport.alignment?.metric_sessions;
  }

  getDefaultPrimarySession(): MetricSession | null {
    return this.getMetricSession(MetricSessionTypes.PRE_ALIGN);
  }

  getMetricSession(sessionName: string): MetricSession | null {
    const foundSession = this.caseReport.alignment?.metric_sessions
      ?.find((metric_session: MetricSession) => metric_session.sessionName === sessionName) || null;

    return foundSession;
  }

  getPreOpData(): AlignmentMetric[] {
    const preOpData = this.getMetricSession(MetricSessionTypes.PRE_ALIGN);
    return preOpData ? preOpData.metrics : [];
  }

  getPostOpValidationSignedUrl(): string | null {
    const postOpSession = this.getMetricSession(MetricSessionTypes.POST_OP_ALIGN);
    return postOpSession?.validationAssetSignedUrl || null;
  }

  getComparisonData(primarySessionName: string, comparisonSessionName: string): MetricComparison[] {
    const comparisons: MetricComparison[] = [];
    const comparisonSession = this.caseReport.alignment.metric_sessions
      .find((metric_session: MetricSession) => metric_session.sessionName === comparisonSessionName);
    if (!comparisonSession) {
      console.error(`CaseReportService: No comparison session found for ${comparisonSessionName}`);
      return [];
    }
    const primarySession = this.caseReport.alignment.metric_sessions
      .find((metric_session: MetricSession) => metric_session.sessionName === primarySessionName);
    if (!primarySession) {
      console.error(`CaseReportService: No session data found for ${primarySessionName}`);
      return [];
    }

    const primaryMetrics: AlignmentMetric[] = this.getMetricsBySessionName(primarySessionName);
    const comparisonMetrics: AlignmentMetric[] = this.getMetricsBySessionName(comparisonSessionName);
    for (const primaryMetric of primaryMetrics) {
      const comparisonMetric = comparisonMetrics
        .find(
          (metric: AlignmentMetric) =>
            metric.label === primaryMetric.label && metric.vertebra === primaryMetric.vertebra
        );

      const comparison: MetricComparison = {
        id: primaryMetric.id,
        label: primaryMetric.label,
        vertebra: primaryMetric.vertebra,
        preValue: primaryMetric.value,
        preValueDesc: (primaryMetric.value === undefined || primaryMetric.value === null) ?
          '-' :
          this.formatValue(primaryMetric.value, primaryMetric.unit),
        postValue: comparisonMetric?.value,
        postValueDesc: (comparisonMetric === undefined || comparisonMetric === null) ?
          '-' :
          `${this.formatValue(comparisonMetric.value, comparisonMetric.unit)}`,
        valueDiffDesc:
          (
            primaryMetric.value === undefined ||
            primaryMetric.value === null ||
            comparisonMetric === undefined ||
            comparisonMetric === null) ?
            '' :
            `${this.formatDifference(
              this.calculateDifference(primaryMetric.value, comparisonMetric?.value), comparisonMetric?.unit
            )?.text}`,
        unit: primaryMetric.unit
      };
      comparisons.push(comparison);
    }

    return comparisons;
  }

  getDefaultSessionForComparison(): MetricSession | null {
    const postOpSession = this.getMetricSession(MetricSessionTypes.POST_OP_ALIGN);
    const otherMetricSessions = this.caseReport.alignment.metric_sessions
      .filter((metric_session: MetricSession) => metric_session.sessionName !== MetricSessionTypes.PRE_ALIGN);
    const preOpSession = this.getMetricSession(MetricSessionTypes.PRE_ALIGN);

    // If there is a post-op session, return that. Otherwise return the first available session that is not pre-op.
    // If no other sessions exist, return the pre-opSession or null,
    return postOpSession ? postOpSession :
      otherMetricSessions.length > 0 ? otherMetricSessions[0] : preOpSession || null;
  }

  getMetricsBySessionName(sessionName: string):  AlignmentMetric[] {
    const foundSession = this.caseReport.alignment.metric_sessions
      .find((metric_session: MetricSession) => metric_session.sessionName === sessionName);
    if (!foundSession) {
      throw new Error(`Session ${sessionName} not found`);
    }
    return foundSession.metrics.filter((metric: AlignmentMetric) => metric.display);
  }

  getVertebraEdgeNodesFromRange = (vertebraRange: string): string[] => {
    const newRange = vertebraRange.replaceAll('S1', SACRUM_LABEL).
      replaceAll('S2', SACRUM_LABEL).
      replaceAll('S3', SACRUM_LABEL);

    if (newRange.includes('-')) {
      return newRange.split('-');
    }

    return [newRange];
  };

  getVertebralPlanes = (comparison: MetricComparison): string[] => {
    const planes: string[] = [];
    const vertebralEdgeNodes = this.getVertebraEdgeNodesFromRange(comparison.vertebra);
    if (vertebralEdgeNodes.length !== 2) {
      return [];
    }

    if (SEGMENTAL_MEASUREMENT_LABELS.includes(comparison.label)) {
      planes.push(`${vertebralEdgeNodes[0]}_superior-endplate-plane`);
      // Special case: no inferior endplate for sacrum
      if (vertebralEdgeNodes[1] === SACRUM_LABEL) {
        planes.push(`${vertebralEdgeNodes[1]}_superior-endplate-plane`);
      } else {
        planes.push(`${vertebralEdgeNodes[1]}_inferior-endplate-plane`);
      }
    } else {
      planes.push(`${vertebralEdgeNodes[0]}_inferior-endplate-plane`);
      planes.push(`${vertebralEdgeNodes[1]}_superior-endplate-plane`);
    }

    return planes;
  };
}
